import { type ReactNode, useMemo } from 'react';
import {
    Button,
    Header,
    Image,
    type ModalConfirmProps,
    type TestProps,
    Text,
    VStack,
} from '@direct-frontend/components';
import { Modal } from '../../ui/Modal';
import styles from './BlockingConfirmModal.module.css';

type BlockingConfirmModalProps = {
    isOpen: boolean;
    onClose: () => void;
    title?: string | ReactNode;
    bodyText?: string | ReactNode;
    imageSrc?: string;
    buttons: ModalConfirmProps['buttons'];
} & TestProps;

// Клон ConfirmModal из дизайн-системы. Блокирует экран. Использовать только при крайней необходимости!
export const BlockingConfirmModal = (props: BlockingConfirmModalProps) => {
    const {
        isOpen,
        onClose,
        imageSrc,
        title,
        bodyText,
        buttons,
        'data-testid': dataTestId = 'BlockingConfirmModal',
    } = props;

    const content = useMemo(() => {
        const header = title ? (
            <Header level="h2" textAlign="center">
                {title}
            </Header>
        ) : null;

        const body = bodyText ? (
            <Text color="gray" role="supplementary" textAlign="center">
                {bodyText}
            </Text>
        ) : null;

        if (header && body) {
            return (
                <VStack gap="6">
                    {header}
                    {body}
                </VStack>
            );
        }

        return header || body;
    }, [title, bodyText]);

    const [firstButton, secondButton, thirdButton] = buttons;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            containerClassName={styles.container}
            dismissOnBack={false}
            dismissOnEsc={false}
            dismissOnOutsideClick={false}
            hasCloseButton={false}
            size="small"
            data-testid={dataTestId}
        >
            {Boolean(imageSrc) && (
                <Image className={styles.image} src={imageSrc} alt="" aria-hidden width={120} height={120} />
            )}
            <VStack gap="28">
                {content}
                <VStack gap="12">
                    <Button
                        {...firstButton}
                        size="l"
                        width="max"
                        data-testid={firstButton['data-testid'] ?? `${dataTestId}.Confirm`}
                    >
                        {firstButton.text ?? 'Да'}
                    </Button>
                    {secondButton && (
                        <Button
                            {...secondButton}
                            size="l"
                            width="max"
                            data-testid={secondButton['data-testid'] ?? `${dataTestId}.Cancel`}
                        >
                            {secondButton.text ?? 'Нет'}
                        </Button>
                    )}
                    {thirdButton && (
                        <Button
                            {...thirdButton}
                            size="l"
                            width="max"
                            data-testid={thirdButton['data-testid'] ?? `${dataTestId}.Additional`}
                        >
                            {thirdButton.text}
                        </Button>
                    )}
                </VStack>
            </VStack>
        </Modal>
    );
};
